import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import useSmqPermissions from "../../../hooks/useSmqPermissions";
import Message from '../../tools/Message';
import DirectionService from "../../../services/DirectionService";
import UserService from "../../../services/UserService";
import ConstatService from "../../../services/smq/ConstatService";
import ExigenceService from "../../../services/smq/ExigenceService";
import ProcessusService from "../../../services/smq/ProcessusService";
import TriggerService from "../../../services/smq/TriggerService";
import ActionStep from "./ActionStep";
import AnalyseStep from "./AnalyseStep";
import ConstatStep from "./ConstatStep";
import CuratifStep from "./CuratifStep";
import OuvertureStep from "./OuvertureStep";
import ProcessusSelection from "./ProcessusSelection";

const STEPS = {
  ACTION: "action",
  ANALYSE: "analyse",
  CONSTAT: "constat",
  CURATIF: "curatif",
  OUVERTURE: "ouverture",
  PROCESSUS: "processus",
};

const WORKFLOW_STEPS = [
  STEPS.CONSTAT,
  STEPS.OUVERTURE,
  STEPS.CURATIF,
  STEPS.ANALYSE,
  STEPS.ACTION,
];

const todayValue = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const emptyAnalysePourquois = () =>
  Array.from({ length: 3 }, () => ({ texte: "", famille_5m: "" }));

const emptyFormData = (processus) => ({
  action_curative_date: "",
  action_curative_description: "",
  action_curative_responsable: "",
  action_curative_statut: "a_faire",
  actions_correctives: [],
  analyse_cause_racine: "",
  analyse_probleme_depart: "",
  analyse_pourquois: emptyAnalysePourquois(),
  constat_date: todayValue(),
  constat_user: "",
  criticite: "",
  description_constat: "",
  description_exigence: "",
  direction_id: processus?.direction_id ?? "",
  exigence_id: "",
  frequence_id: "",
  gravite_id: "",
  nc_status: "open",
  processus_id: processus?.processus_id ?? "",
  trigger_id: "",
});

const responseArray = (payload, label) => {
  if (!Array.isArray(payload?.data)) {
    throw new Error(payload?.message || `${label} a retourné une réponse invalide.`);
  }
  return payload.data;
};

const normalizeUser = (user) => {
  const composedName = [user?.first_name, user?.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  const name =
    String(user?.name || user?.identity || "").trim() ||
    composedName ||
    "Utilisateur sans nom";
  const fonction = String(
    user?.function_name || user?.fonction || user?.function || ""
  ).trim();
  const directionCode = String(
    user?.direction_code || user?.direction || ""
  ).trim();

  return {
    directionCode,
    functionName: fonction,
    id: user?.user_id,
    label: name,
  };
};

const formDataFromConstat = (constat) => ({
  ...emptyFormData(null),
  action_curative_date: constat?.action_curative_date || "",
  action_curative_description: constat?.action_curative_description || "",
  action_curative_responsable:
    constat?.action_curative_responsable ?? "",
  action_curative_statut: constat?.action_curative_statut || "a_faire",
  analyse_cause_racine: constat?.analyse_cause_racine || "",
  analyse_probleme_depart: constat?.analyse_probleme_depart || "",
  analyse_pourquois:
    Array.isArray(constat?.analyse_pourquois) && constat.analyse_pourquois.length
      ? constat.analyse_pourquois
      : emptyAnalysePourquois(),
  actions_correctives:
    Array.isArray(constat?.actions_correctives) && constat.actions_correctives.length
      ? constat.actions_correctives.map((action, index) => ({
          action_corrective: action.action_corrective || "",
          avancement: action.avancement ?? "",
          date_debut: action.date_debut || "",
          date_fin: action.date_fin || "",
          deletionLocked: false,
          indicateur_efficacite: action.indicateur_efficacite || "",
          isEditing: false,
          ordre: action.ordre || index + 1,
          plan_action: action.plan_action || "",
          plan_action_id: action.plan_action_id,
          responsable_ids: Array.isArray(action.responsable_ids)
            ? action.responsable_ids
            : action.responsable_id
              ? [action.responsable_id]
              : [],
          responsables: Array.isArray(action.responsables)
            ? action.responsables
            : [],
          status: action.status || "confirmed",
          validated: action.status === "confirmed",
        }))
      : [],
  constat_date: constat?.constat_date || todayValue(),
  constat_user: constat?.constat_user ?? "",
  criticite: constat?.criticite || "",
  description_constat: constat?.description_constat || "",
  description_exigence: constat?.description_exigence || "",
  direction_id: constat?.direction_id ?? "",
  exigence_id: constat?.exigence_id ?? "",
  frequence_id: constat?.frequence ?? "",
  gravite_id: constat?.gravite ?? "",
  nc_status: constat?.statut || "open",
  processus_id: constat?.processus_id ?? "",
  trigger_id: constat?.trigger_id ?? "",
});

const NCAdd = () => {
  const { canCreate, canTreat } = useSmqPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(STEPS.PROCESSUS);
  const [initialProcessusId, setInitialProcessusId] = useState(null);
  const [formData, setFormData] = useState(() => emptyFormData(null));
  const [nextIndex, setNextIndex] = useState(null);
  const [nextIndexLoading, setNextIndexLoading] = useState(false);
  const [nextIndexError, setNextIndexError] = useState(null);
  const [savedConstat, setSavedConstat] = useState(null);
  const [savedCuratif, setSavedCuratif] = useState(null);
  const [savedAnalyse, setSavedAnalyse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [statusMessage, setStatusMessage] = useState({
    color: "#2e7d32",
    destination: null,
    messageInfo: "",
    open: false,
  });

  const [processus, setProcessus] = useState([]);
  const [processusLoading, setProcessusLoading] = useState(true);
  const [processusError, setProcessusError] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [triggersLoading, setTriggersLoading] = useState(true);
  const [triggersError, setTriggersError] = useState(null);
  const [directions, setDirections] = useState([]);
  const [directionsLoading, setDirectionsLoading] = useState(true);
  const [directionsError, setDirectionsError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [exigences, setExigences] = useState([]);
  const [exigencesLoading, setExigencesLoading] = useState(false);
  const [exigencesError, setExigencesError] = useState(null);

  const processusRequestRef = useRef(0);
  const triggersRequestRef = useRef(0);
  const directionsRequestRef = useRef(0);
  const usersRequestRef = useRef(0);
  const nextIndexRequestRef = useRef(0);
  const exigencesRequestRef = useRef(0);
  const directAccessLoadedRef = useRef(null);

  const directAccess = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      constatId: params.get("constat_id"),
      step: params.get("step"),
    };
  }, [location.search]);

  const selectedProcessus = useMemo(
    () =>
      processus.find(
        (item) => String(item.processus_id) === String(formData.processus_id)
      ) || null,
    [formData.processus_id, processus]
  );

  const loadProcessus = useCallback(async () => {
    const requestId = processusRequestRef.current + 1;
    processusRequestRef.current = requestId;
    setProcessusLoading(true);
    setProcessusError(null);

    try {
      const rows = responseArray(await ProcessusService.getProcessus(), "Le service des processus");
      if (requestId === processusRequestRef.current) setProcessus(rows);
    } catch (error) {
      if (requestId === processusRequestRef.current) {
        setProcessus([]);
        setProcessusError(error?.message || "Impossible de charger la liste des processus.");
      }
    } finally {
      if (requestId === processusRequestRef.current) setProcessusLoading(false);
    }
  }, []);

  const loadTriggers = useCallback(async () => {
    const requestId = triggersRequestRef.current + 1;
    triggersRequestRef.current = requestId;
    setTriggersLoading(true);
    setTriggersError(null);

    try {
      const rows = responseArray(await TriggerService.getAll(), "Le service des déclencheurs");
      if (requestId === triggersRequestRef.current) setTriggers(rows);
    } catch (error) {
      if (requestId === triggersRequestRef.current) {
        setTriggers([]);
        setTriggersError(error?.message || "Impossible de charger les déclencheurs.");
      }
    } finally {
      if (requestId === triggersRequestRef.current) setTriggersLoading(false);
    }
  }, []);

  const loadDirections = useCallback(async () => {
    const requestId = directionsRequestRef.current + 1;
    directionsRequestRef.current = requestId;
    setDirectionsLoading(true);
    setDirectionsError(null);

    try {
      const rows = responseArray(await DirectionService.getDirection(), "Le service des directions");
      if (requestId === directionsRequestRef.current) setDirections(rows);
    } catch (error) {
      if (requestId === directionsRequestRef.current) {
        setDirections([]);
        setDirectionsError(error?.message || "Impossible de charger les directions.");
      }
    } finally {
      if (requestId === directionsRequestRef.current) setDirectionsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    const requestId = usersRequestRef.current + 1;
    usersRequestRef.current = requestId;
    setUsersLoading(true);
    setUsersError(null);

    try {
      const rows = responseArray(
        await UserService.getAllUserlist(0, 0, "all"),
        "Le service des utilisateurs"
      );
      if (requestId === usersRequestRef.current) {
        setUsers(
          rows
            .map(normalizeUser)
            .filter((option) => option.id !== null && option.id !== undefined)
            .sort((left, right) =>
              left.label.localeCompare(right.label, "fr", { sensitivity: "base" })
            )
        );
      }
    } catch (error) {
      if (requestId === usersRequestRef.current) {
        setUsers([]);
        setUsersError(error?.message || "Impossible de charger les utilisateurs.");
      }
    } finally {
      if (requestId === usersRequestRef.current) setUsersLoading(false);
    }
  }, []);

  const loadNextIndex = useCallback(async (triggerId) => {
    const requestId = nextIndexRequestRef.current + 1;
    nextIndexRequestRef.current = requestId;

    if (!triggerId) {
      setNextIndex(null);
      setNextIndexError(null);
      setNextIndexLoading(false);
      return;
    }

    setNextIndexLoading(true);
    setNextIndexError(null);
    setNextIndex(null);

    try {
      const payload = await ConstatService.getNextIndex(triggerId);
      const value = Number(payload?.data?.next_index);

      if (!Number.isInteger(value) || value < 1) {
        throw new Error("Le prochain index NC est invalide.");
      }

      if (requestId === nextIndexRequestRef.current) setNextIndex(value);
    } catch (error) {
      if (requestId === nextIndexRequestRef.current) {
        setNextIndex(null);
        setNextIndexError(
          error?.message || "Impossible de récupérer le prochain index NC."
        );
      }
    } finally {
      if (requestId === nextIndexRequestRef.current) setNextIndexLoading(false);
    }
  }, []);

  const loadExigences = useCallback(async () => {
    const requestId = exigencesRequestRef.current + 1;
    exigencesRequestRef.current = requestId;
    setExigencesLoading(true);
    setExigencesError(null);

    try {
      const rows = responseArray(
        await ExigenceService.getAll(),
        "Le service des exigences"
      );
      if (requestId === exigencesRequestRef.current) setExigences(rows);
    } catch (error) {
      if (requestId === exigencesRequestRef.current) {
        setExigences([]);
        setExigencesError(
          error?.message || "Impossible de charger les types d’exigence."
        );
      }
    } finally {
      if (requestId === exigencesRequestRef.current) setExigencesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcessus();
    loadTriggers();
    loadDirections();
    loadUsers();

    return () => {
      processusRequestRef.current += 1;
      triggersRequestRef.current += 1;
      directionsRequestRef.current += 1;
      usersRequestRef.current += 1;
      nextIndexRequestRef.current += 1;
      exigencesRequestRef.current += 1;
    };
  }, [loadDirections, loadProcessus, loadTriggers, loadUsers]);

  useEffect(() => {
    if (
      !directAccess.constatId ||
      ![STEPS.CONSTAT, STEPS.ANALYSE].includes(directAccess.step)
    ) {
      return;
    }
    const accessKey = `${directAccess.constatId}:${directAccess.step}`;
    if (directAccessLoadedRef.current === accessKey) return;
    directAccessLoadedRef.current = accessKey;

    let active = true;
    setSaving(true);
    ConstatService.getConstat(directAccess.constatId)
      .then((response) => {
        if (!active) return;
        const constat = response?.data;
        if (!constat?.constat_id || !constat?.reference) {
          throw new Error("La réponse de chargement de la NC est invalide.");
        }
        setFormData(formDataFromConstat(constat));
        setInitialProcessusId(constat.processus_id ?? null);
        setSavedConstat({
          constat_id: constat.constat_id,
          index_ordre: Number(constat.index_ordre),
          permissions: constat.permissions,
          reference: constat.reference,
          status: constat.statut || "open",
        });
        setSavedCuratif(
          constat.curatif_id
            ? {
                constat_id: constat.constat_id,
                curatif_id: constat.curatif_id,
              }
            : null
        );
        setSavedAnalyse(
          constat.analyse_id
            ? {
                analyse_id: constat.analyse_id,
                constat_id: constat.constat_id,
              }
            : null
        );
        setNextIndex(Number(constat.index_ordre));
        setCurrentStep(directAccess.step);
      })
      .catch((error) => {
        if (!active) return;
        setStatusMessage({
          color: "#d32f2f",
          destination: null,
          messageInfo: error?.message || "Impossible de charger la non-conformité.",
          open: true,
        });
      })
      .finally(() => {
        if (active) setSaving(false);
      });

    return () => {
      active = false;
    };
  }, [directAccess.constatId, directAccess.step]);

  const handleInitialProcessusSelect = (selected) => {
    if (!selected) return;
    setInitialProcessusId(selected.processus_id);
    setFormData(emptyFormData(selected));
    setSavedCuratif(null);
    setSavedAnalyse(null);
    setNextIndex(null);
    setNextIndexError(null);
    setCurrentStep(STEPS.CONSTAT);
  };

  const handleProcessusChange = (selected) => {
    setFormData((current) => ({
      ...current,
      direction_id: selected?.direction_id ?? "",
      processus_id: selected?.processus_id ?? "",
    }));
  };

  const handleFormChange = (name, value) => {
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleTriggerChange = (triggerId) => {
    handleFormChange("trigger_id", triggerId || "");
    if (!savedConstat?.constat_id) {
      loadNextIndex(triggerId);
    }
  };

  const handleAnalyseWhyChange = (index, patch) => {
    setFormData((current) => ({
      ...current,
      analyse_pourquois: Array.from(
        {
          length: Math.max(
            index + 1,
            current.analyse_pourquois?.length || 0,
            3
          ),
        },
        (_, itemIndex) => {
          const item = current.analyse_pourquois?.[itemIndex] || {
            texte: "",
            famille_5m: "",
          };
          return itemIndex === index ? { ...item, ...patch } : item;
        }
      ),
    }));
  };

  const handleAnalyseWhyAdd = () => {
    setFormData((current) => ({
      ...current,
      analyse_pourquois: [
        ...(current.analyse_pourquois || []),
        { texte: "", famille_5m: "" },
      ],
    }));
  };

  const handleAnalyseWhyRemove = (index) => {
    setFormData((current) => {
      const nextPourquois = (current.analyse_pourquois || []).filter(
        (_, itemIndex) => itemIndex !== index
      );
      return {
        ...current,
        analyse_pourquois: nextPourquois.length
          ? nextPourquois
          : emptyAnalysePourquois(),
      };
    });
  };

  const handleActionRemove = (index) => {
    setFormData((current) => {
      const nextActions = (current.actions_correctives || []).filter(
        (_, itemIndex) => itemIndex !== index
      );
      return {
        ...current,
        actions_correctives: nextActions,
      };
    });
  };

  const resetConstat = () => {
    const initialProcessus = processus.find(
      (item) => String(item.processus_id) === String(initialProcessusId)
    );
    setFormData(emptyFormData(initialProcessus));
  };

  const resetOuverture = () => {
    setFormData((current) => ({
      ...current,
      criticite: "",
      description_exigence: "",
      exigence_id: "",
      frequence_id: "",
      gravite_id: "",
    }));
  };

  const goToOuverture = () => {
    setCurrentStep(STEPS.OUVERTURE);
    if (!exigences.length && !exigencesLoading) loadExigences();
  };

  const saveConstatStep = async () => {
    const canEditDeclaration = savedConstat?.constat_id
      ? Boolean(savedConstat?.permissions?.can_edit_declaration || canTreat)
      : canCreate;
    if (!canEditDeclaration) return;
    if (saving) return;

    const payload = {
      trigger_id: formData.trigger_id,
      processus_id: formData.processus_id,
      direction_id: formData.direction_id,
      constat_user: formData.constat_user,
      constat_date: formData.constat_date,
      description_constat: String(formData.description_constat || "").trim(),
    };

    setSaving(true);
    try {
      const response = savedConstat?.constat_id
        ? await ConstatService.updateConstatStep(savedConstat.constat_id, payload)
        : await ConstatService.createConstatStep(payload);
      const result = response?.data;

      if (!result?.constat_id || !result?.reference) {
        throw new Error("La réponse d’enregistrement du constat est invalide.");
      }

      setSavedConstat((current) => ({
        ...current,
        ...result,
        status: result.status || current?.status || "open",
      }));
      setFormData((current) => ({
        ...current,
        nc_status: result.status || current.nc_status || "open",
      }));
      setNextIndex(Number(result.index_ordre));
      setNextIndexError(null);
      setStatusMessage({
        color: "#2e7d32",
        destination: "ouverture",
        messageInfo: response?.message || "Constat enregistré avec succès.",
        open: true,
      });
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: error?.message || "Impossible d’enregistrer le constat.",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveConstat = async (destination) => {
    const declarationPermission = savedConstat?.permissions;
    const canEditDeclaration = declarationPermission
      ? Boolean(declarationPermission.can_edit_declaration || canTreat)
      : canCreate;
    if (
      !canEditDeclaration ||
      saving ||
      !savedConstat?.constat_id
    ) return;

    const payload = {
      trigger_id: formData.trigger_id,
      processus_id: formData.processus_id,
      direction_id: formData.direction_id,
      constat_user: formData.constat_user,
      constat_date: formData.constat_date,
      description_constat: String(formData.description_constat || "").trim(),
      exigence_id: formData.exigence_id,
      description_exigence: String(formData.description_exigence || "").trim(),
      gravite: formData.gravite_id,
      frequence: formData.frequence_id,
      criticite: formData.criticite,
    };

    setSaving(true);
    try {
      const response = await ConstatService.updateConstat(
        savedConstat.constat_id,
        payload
      );
      const result = response?.data;

      if (!result?.constat_id || !result?.reference) {
        throw new Error("La réponse d’enregistrement de la NC est invalide.");
      }

      setSavedConstat((current) => ({
        ...current,
        ...result,
        status: result.status || current?.status || formData.nc_status || "open",
      }));
      setFormData((current) => ({
        ...current,
        nc_status: result.status || current.nc_status || "open",
      }));
      setNextIndex(Number(result.index_ordre));
      setStatusMessage({
        color: "#2e7d32",
        destination,
        messageInfo: response?.message || "Non-conformité enregistrée avec succès.",
        open: true,
      });
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: error?.message || "Impossible d’enregistrer la non-conformité.",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveCuratif = async (destination) => {
    if (!canTreat || saving || !savedConstat?.constat_id) return;

    const payload = {
      description: String(formData.action_curative_description || "").trim(),
      responsable_id: formData.action_curative_responsable,
      date_realisation: formData.action_curative_date,
      statut: formData.action_curative_statut,
    };

    setSaving(true);
    try {
      const response = savedCuratif?.curatif_id
        ? await ConstatService.updateCuratif(savedConstat.constat_id, payload)
        : await ConstatService.createCuratif({
            ...payload,
            constat_id: savedConstat.constat_id,
          });
      const result = response?.data;

      if (!result?.curatif_id || !result?.constat_id) {
        throw new Error("La réponse d’enregistrement du curatif est invalide.");
      }

      setSavedCuratif(result);
      setSavedConstat((current) => ({
        ...current,
        status: result.status || "in_progress",
      }));
      setFormData((current) => ({
        ...current,
        nc_status: result.status || "in_progress",
      }));
      setStatusMessage({
        color: "#2e7d32",
        destination,
        messageInfo:
          response?.message || "Action curative enregistrée avec succès.",
        open: true,
      });
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo:
          error?.message || "Impossible d’enregistrer l’action curative.",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const saveAnalyse = async (destination) => {
    if (!canTreat || saving || !savedConstat?.constat_id) return;

    const pourquois = (formData.analyse_pourquois || [])
      .map((item, index) => ({
        famille_5m: String(item?.famille_5m || "").trim(),
        ordre: index + 1,
        texte: String(item?.texte || "").trim(),
      }))
      .filter((item) => item.texte || item.famille_5m);
    const payload = {
      cause_racine: String(formData.analyse_cause_racine || "").trim(),
      pourquois,
      probleme_depart: String(formData.analyse_probleme_depart || "").trim(),
    };

    setSaving(true);
    try {
      const response = savedAnalyse?.analyse_id
        ? await ConstatService.updateAnalyse(savedConstat.constat_id, payload)
        : await ConstatService.createAnalyse({
            ...payload,
            constat_id: savedConstat.constat_id,
          });
      const result = response?.data;

      if (!result?.analyse_id || !result?.constat_id) {
        throw new Error("La réponse d’enregistrement de l’analyse est invalide.");
      }

      setSavedAnalyse(result);
      setStatusMessage({
        color: "#2e7d32",
        destination,
        messageInfo: response?.message || "Analyse enregistrée avec succès.",
        open: true,
      });
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: error?.message || "Impossible d’enregistrer l’analyse.",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const actionPayload = (action, ordre) => ({
    action_corrective: String(action.action_corrective || "").trim(),
    avancement: Number(action.avancement),
    date_debut: action.date_debut,
    date_fin: action.date_fin,
    indicateur_efficacite: String(action.indicateur_efficacite || "").trim(),
    ordre,
    plan_action: String(action.plan_action || "").trim(),
    responsable_ids: Array.isArray(action.responsable_ids)
      ? action.responsable_ids
      : [],
  });

  const notificationFeedback = (response, successMessage) => {
    const failed = Number(response?.data?.notification?.failed || 0);
    return {
      color: failed > 0 ? "#ed6c02" : "#2e7d32",
      messageInfo:
        failed > 0
          ? "Action enregistrée, mais certaines notifications n’ont pas pu être envoyées."
          : successMessage,
    };
  };
  const handleActionSave = async (action, index = null) => {
    if (!canTreat || !action || saving || !savedConstat?.constat_id) return false;

    setSaving(true);
    try {
      const nextOrder =
        Math.max(
          0,
          ...(formData.actions_correctives || []).map(
            (item) => Number(item.ordre) || 0
          )
        ) + 1;
      const ordre = action.ordre || nextOrder;
      const payload = actionPayload(action, ordre);
      const response = action.plan_action_id
        ? await ConstatService.updateAction(action.plan_action_id, payload)
        : await ConstatService.createAction({
            ...payload,
            constat_id: savedConstat.constat_id,
          });
      const result = response?.data;
      if (!result?.plan_action_id) {
        throw new Error("Invalid corrective action response");
      }
      const savedAction = {
        ...action,
        ...result,
        deletionLocked: false,
        isEditing: false,
        ordre: result.ordre || ordre,
        plan_action_id: result.plan_action_id,
        responsable_ids:
          result.responsable_ids || action.responsable_ids || [],
        responsables: result.responsables || action.responsables || [],
        status: result.status,
        validated: result.status === "confirmed",
      };
      setFormData((current) => ({
        ...current,
        actions_correctives:
          index === null
            ? [...(current.actions_correctives || []), savedAction]
            : (current.actions_correctives || []).map((item, itemIndex) =>
                itemIndex === index ? savedAction : item
              ),
      }));
      const feedback = notificationFeedback(
        response,
        action.plan_action_id
          ? "Action corrective modifiée."
          : "Action corrective enregistrée."
      );
      setStatusMessage({
        color: feedback.color,
        destination: null,
        messageInfo: feedback.messageInfo,
        open: true,
      });
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Unable to save corrective action", error);
      }
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: "Impossible d’enregistrer l’action corrective.",
        open: true,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };
  const handleActionValidate = async (action, index) => {
    if (
      !canTreat ||
      !action?.plan_action_id ||
      action.status === "confirmed" ||
      saving
    ) {
      return false;
    }

    setSaving(true);
    try {
      const response = await ConstatService.validateAction(
        action.plan_action_id
      );
      const result = response?.data;
      if (!result?.plan_action_id || result.status !== "confirmed") {
        throw new Error("Invalid corrective action validation response");
      }
      setFormData((current) => ({
        ...current,
        actions_correctives: (current.actions_correctives || []).map(
          (item, itemIndex) =>
            itemIndex === index
              ? {
                  ...item,
                  ...result,
                  deletionLocked: false,
                  isEditing: false,
                  validated: true,
                }
              : item
        ),
      }));
      setStatusMessage({
        color: "#2e7d32",
        destination: null,
        messageInfo: "Action corrective validée.",
        open: true,
      });
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Unable to validate corrective action", error);
      }
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: "Impossible de valider l’action corrective.",
        open: true,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };
  const handleActionDelete = async (index) => {
    const action = formData.actions_correctives[index];
    if (!action || saving) return false;
    if (!action.plan_action_id) {
      handleActionRemove(index);
      return true;
    }

    setSaving(true);
    try {
      await ConstatService.deleteAction(action.plan_action_id);
      handleActionRemove(index);
      setStatusMessage({
        color: "#2e7d32",
        destination: null,
        messageInfo: "Action corrective supprimée.",
        open: true,
      });
      return true;
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: "Impossible de supprimer l'action corrective.",
        open: true,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveActions = async (destination) => {
    if (!canTreat || saving || !savedConstat?.constat_id) return;
    const actions = formData.actions_correctives || [];
    if (!actions.length) return;

    const hasPersistedActions = actions.some(
      (action) => Boolean(action.plan_action_id)
    );
    const payload = {
      actions: actions.map((action, index) => {
        const pending =
          !action.plan_action_id || !action.validated || action.isEditing;
        return {
          ...actionPayload(action, index + 1),
          ...(action.plan_action_id
            ? { plan_action_id: action.plan_action_id }
            : {}),
          ...(pending ? { status: "pending" } : {}),
        };
      }),
    };

    setSaving(true);
    try {
      const response = hasPersistedActions
        ? await ConstatService.updateActions(savedConstat.constat_id, payload)
        : await ConstatService.createActions({
            constat_id: savedConstat.constat_id,
            ...payload,
          });
      const savedActions = Array.isArray(response?.data?.actions)
        ? response.data.actions
        : [];
      const savedByOrder = new Map(
        savedActions.map((action) => [Number(action.ordre), action])
      );
      setFormData((current) => ({
        ...current,
        actions_correctives: (current.actions_correctives || []).map(
          (action, index) => {
            const savedAction = savedByOrder.get(index + 1);
            if (!savedAction) return action;
            return {
              ...action,
              ordre: savedAction.ordre,
              plan_action_id: savedAction.plan_action_id,
              responsable_ids:
                savedAction.responsable_ids || action.responsable_ids || [],
              responsables:
                savedAction.responsables || action.responsables || [],
              status: savedAction.status,
            };
          }
        ),
      }));
      const feedback = notificationFeedback(
        response,
        "Actions correctives enregistrées."
      );
      setStatusMessage({
        color: feedback.color,
        destination,
        messageInfo: feedback.messageInfo,
        open: true,
      });
    } catch (error) {
      setStatusMessage({
        color: "#d32f2f",
        destination: null,
        messageInfo: error?.message || "Impossible d’enregistrer les actions.",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const closeStatusMessage = () => {
    const { destination } = statusMessage;
    setStatusMessage((current) => ({ ...current, open: false }));

    if (!destination) return;
    if (destination === "registre") {
      setClosing(true);
      navigate("/smq_nc/registre");
    } else if (destination === "ouverture") {
      goToOuverture();
    } else if (destination === "curatif") {
      setCurrentStep(STEPS.CURATIF);
    } else if (destination === "analyse") {
      setCurrentStep(STEPS.ANALYSE);
    } else if (destination === "action") {
      setCurrentStep(STEPS.ACTION);
    }
  };

  const redirecting = Boolean(
    closing || (statusMessage.open && statusMessage.destination)
  );
  const busy = saving || redirecting;
  const stepNavigationEnabled = Boolean(savedConstat?.constat_id) && !busy;

  const handleStepChange = (stepIndex) => {
    if (!stepNavigationEnabled) return;

    const nextStep = WORKFLOW_STEPS[stepIndex];
    if (!nextStep || nextStep === currentStep) return;
    if (
      [STEPS.CONSTAT, STEPS.OUVERTURE].includes(nextStep) &&
      !canCreate &&
      !savedConstat?.permissions?.can_edit_declaration
    ) return;
    if ([STEPS.CURATIF, STEPS.ANALYSE, STEPS.ACTION].includes(nextStep) && !canTreat) return;

    if (nextStep === STEPS.OUVERTURE && !exigences.length && !exigencesLoading) {
      loadExigences();
    }
    setCurrentStep(nextStep);
  };

  const handleClose = () => {
    if (busy) return;
    setClosing(true);
    navigate("/smq_nc/registre");
  };

  return (
    <SMQMainContainer title="Nouvelle non-conformité">
      <Message
        color={statusMessage.color}
        handleClose={closeStatusMessage}
        messageInfo={statusMessage.messageInfo}
        open={statusMessage.open}
      />
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: "#f1f5fb",
          display: "flex",
          gap: 2,
          justifyContent: savedConstat?.reference ? "space-between" : "flex-end",
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
        }}
      >
        {savedConstat?.reference && (
          <Box
            sx={{
              // backgroundColor: "#e3f4f7",
              border: "1px solid #9dd6de",
              borderRadius: "8px",
              px: 1.5,
              py: 0.8,
            }}
          >
            <Typography color="#173d61" fontSize={14} fontWeight={800}>
              {savedConstat.reference}
            </Typography>
          </Box>
        )}
        <Button
          aria-label="Fermer la saisie et retourner au registre"
          color="error"
          disabled={busy}
          onClick={handleClose}
          startIcon={
            closing ? <CircularProgress color="inherit" size={16} /> : <CloseIcon />
          }
          variant="outlined"
        >
          Fermer
        </Button>
      </Box>
      {currentStep === STEPS.ACTION && savedConstat ? (
        <ActionStep
          formData={formData}
          navigationEnabled={stepNavigationEnabled}
          onActionDelete={handleActionDelete}
          onActionValidate={handleActionValidate}
          onActionSave={handleActionSave}
          onBack={() => setCurrentStep(STEPS.ANALYSE)}
          onRetryUsers={loadUsers}
          onSave={saveActions}
          onStepChange={handleStepChange}
          saving={busy}
          users={users}
          usersError={usersError}
          usersLoading={usersLoading}
        />
      ) : currentStep === STEPS.ANALYSE && savedConstat ? (
        <AnalyseStep
          formData={formData}
          navigationEnabled={stepNavigationEnabled}
          onBack={() => setCurrentStep(STEPS.CURATIF)}
          onChange={handleFormChange}
          onSave={saveAnalyse}
          onStepChange={handleStepChange}
          onWhyAdd={handleAnalyseWhyAdd}
          onWhyChange={handleAnalyseWhyChange}
          onWhyRemove={handleAnalyseWhyRemove}
          saving={busy}
        />
      ) : currentStep === STEPS.CURATIF && savedConstat ? (
        <CuratifStep
          canSkip={!savedCuratif?.curatif_id}
          formData={formData}
          isLocked={formData.nc_status === "done"}
          navigationEnabled={stepNavigationEnabled}
          onBack={() => setCurrentStep(STEPS.OUVERTURE)}
          onChange={handleFormChange}
          onNext={() => setCurrentStep(STEPS.ANALYSE)}
          onRetryUsers={loadUsers}
          onSave={saveCuratif}
          onStepChange={handleStepChange}
          saving={busy}
          users={users}
          usersError={usersError}
          usersLoading={usersLoading}
        />
      ) : currentStep === STEPS.OUVERTURE ? (
        <OuvertureStep
          exigences={exigences}
          exigencesError={exigencesError}
          exigencesLoading={exigencesLoading}
          formData={formData}
          navigationEnabled={stepNavigationEnabled}
          onBack={() => setCurrentStep(STEPS.CONSTAT)}
          onChange={handleFormChange}
          onReset={resetOuverture}
          onRetryExigences={loadExigences}
          onSave={saveConstat}
          onStepChange={handleStepChange}
          saving={busy}
        />
      ) : currentStep === STEPS.CONSTAT && selectedProcessus ? (
        <ConstatStep
          directions={directions}
          directionsError={directionsError}
          directionsLoading={directionsLoading}
          formData={formData}
          navigationEnabled={stepNavigationEnabled}
          nextIndex={nextIndex}
          nextIndexError={nextIndexError}
          nextIndexLoading={nextIndexLoading}
          onChange={handleFormChange}
          onNext={saveConstatStep}
          onProcessusChange={handleProcessusChange}
          onReset={resetConstat}
          onRetryNextIndex={() => loadNextIndex(formData.trigger_id)}
          onRetryDirections={loadDirections}
          onRetryTriggers={loadTriggers}
          onRetryUsers={loadUsers}
          onStepChange={handleStepChange}
          onTriggerChange={handleTriggerChange}
          processus={processus}
          savedReference={savedConstat?.reference || ""}
          saving={busy}
          triggers={triggers}
          triggersError={triggersError}
          triggersLoading={triggersLoading}
          users={users}
          usersError={usersError}
          usersLoading={usersLoading}
        />
      ) : (
        <ProcessusSelection
          error={processusError}
          loading={processusLoading}
          onRetry={loadProcessus}
          onSelect={handleInitialProcessusSelect}
          processus={processus}
          selectedProcessusId={selectedProcessus?.processus_id ?? ""}
        />
      )}
    </SMQMainContainer>
  );
};

export default NCAdd;
